import React, { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  crossValidateDocuments,
  getCrossValidationJobStatus,
  useCrossValidationJobSubscription,
  ShipmentDocument,
} from '@wove/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  JobProgress,
  Alert,
  Badge,
  cn,
} from '@wove/web-ui'
import { Shield, AlertCircle } from 'lucide-react'
import { CrossValidationResults } from './CrossValidationResults'

interface CrossValidationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shipmentId: string
  documentIds: string[]
  documents: ShipmentDocument[]
}

export const CrossValidationDialog: React.FC<CrossValidationDialogProps> = ({
  open,
  onOpenChange,
  shipmentId,
  documentIds,
  documents,
}) => {
  const [jobId, setJobId] = useState<string | null>(null)
  const [pollingJobStatus, setPollingJobStatus] = useState<any>(null)

  // Subscribe to job updates
  const { jobStatus, isCompleted, isFailed } = useCrossValidationJobSubscription(jobId || undefined)

  // Mutation to start cross-validation job
  const startJobMutation = useMutation({
    mutationFn: () => crossValidateDocuments(shipmentId, documentIds),
    onSuccess: (data) => {
      if (data.success && data.jobId) {
        setJobId(data.jobId)
      }
    },
  })

  // Poll for job status if subscription fails
  useEffect(() => {
    if (!jobId || jobStatus) return

    const pollStatus = async () => {
      try {
        const result = await getCrossValidationJobStatus(shipmentId, jobId)
        console.log('Polling result:', result)
        if (result.success) {
          setPollingJobStatus(result.job)
        }
      } catch (error) {
        console.error('Failed to poll job status:', error)
      }
    }

    // Initial poll
    pollStatus()

    // Set up polling interval
    const interval = setInterval(pollStatus, 2000)

    return () => clearInterval(interval)
  }, [jobId, jobStatus, shipmentId])

  // Use subscription status if available, otherwise use polling status
  const currentStatus = jobStatus || pollingJobStatus

  console.log('CrossValidationDialog - jobStatus:', jobStatus)
  console.log('CrossValidationDialog - pollingJobStatus:', pollingJobStatus)
  console.log('CrossValidationDialog - currentStatus:', currentStatus)

  // Start job when dialog opens
  useEffect(() => {
    if (open && !jobId && !startJobMutation.isPending) {
      startJobMutation.mutate()
    }
  }, [open])

  // Reset when dialog closes
  useEffect(() => {
    if (!open) {
      setJobId(null)
      setPollingJobStatus(null)
      startJobMutation.reset()
    }
  }, [open])

  const selectedDocs = documents.filter((doc) => documentIds.includes(doc.id))

  const renderResults = () => {
    console.log('CrossValidationDialog - currentStatus:', currentStatus)
    if (!currentStatus?.result) return null

    return (
      <div className="mt-4">
        <CrossValidationResults results={currentStatus.result} documents={documents} />
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Cross-Validate Documents
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <div className="space-y-4">
            {/* Selected documents */}
            <div>
              <h4 className="text-sm font-medium mb-2">
                Selected Documents ({selectedDocs.length})
              </h4>
              <div className="space-y-1">
                {selectedDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <span>{doc.name}</span>
                    <Badge variant="secondary">{doc.type}</Badge>
                  </div>
                ))}
              </div>
            </div>

            {/* Job progress */}
            {(currentStatus || startJobMutation.isPending) && (
              <JobProgress
                status={startJobMutation.isPending ? 'pending' : currentStatus?.status || 'pending'}
                progress={currentStatus?.progress || 0}
                error={currentStatus?.error}
                title="Cross-validation in progress"
                subtitle="Comparing and validating fields across selected documents"
              />
            )}

            {/* Results */}
            {currentStatus?.status === 'completed' && renderResults()}

            {/* Error */}
            {startJobMutation.error && (
              <Alert variant="error">
                <AlertCircle className="h-4 w-4" />
                <div>
                  <strong>Failed to start cross-validation</strong>
                  <p className="text-sm mt-1">{(startJobMutation.error as any)?.message}</p>
                </div>
              </Alert>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {currentStatus?.status === 'completed' || currentStatus?.status === 'failed'
              ? 'Close'
              : 'Cancel'}
          </Button>
          {currentStatus?.status === 'failed' && (
            <Button onClick={() => startJobMutation.mutate()}>Retry</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
